import React, {useEffect, useState} from 'react';
import {Box, Button, Header, Icon, SpaceBetween, Spinner, StatusIndicator} from '@cloudscape-design/components';
import {Credentials} from 'aws-sdk';
import {ChartRenderer} from './chart-renderer';
import {getArtifactContent, getArtifactDownloadUrl} from './cassandra-api';
import {CHART_MIME_TYPE, VALID_CHART_TYPES} from './cassandra-types';

function getTypeLabel(mimeType: string): string {
    if (mimeType === CHART_MIME_TYPE) return 'Chart';
    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType === 'text/csv' || mimeType.includes('spreadsheet')) return 'Table';
    if (mimeType === 'application/json') return 'JSON';
    if (mimeType.startsWith('text/')) return 'Text';
    return 'File';
}

function getIcon(mimeType: string): React.ReactNode {
    if (mimeType === CHART_MIME_TYPE) return <Icon name="insert-row" />;
    if (mimeType.startsWith('image/')) return <Icon name="file" variant="subtle" />;
    if (mimeType === 'text/csv' || mimeType.includes('spreadsheet')) return <Icon name="view-vertical" />;
    if (mimeType === 'application/json') return <Icon name="script" />;
    if (mimeType.startsWith('text/')) return <Icon name="file" />;
    return <Icon name="file" />;
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

interface ArtifactPreviewProps {
    artifactId: string;
    name: string;
    mimeType: string;
    sizeBytes: number;
    chatId: string;
    credentials: Credentials;
    renderInline?: boolean;
    onClick?: (artifact: {artifactId: string; name: string; mimeType: string}) => void;
}

export default function ArtifactPreview({
    artifactId,
    name,
    mimeType,
    sizeBytes,
    chatId,
    credentials,
    renderInline = false,
    onClick,
}: ArtifactPreviewProps) {
    const [chartData, setChartData] = useState<{
        type: string;
        props: any;
    } | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);

    const isChart = mimeType === CHART_MIME_TYPE;

    useEffect(() => {
        if (!isChart || !renderInline) return;
        setLoading(true);
        getArtifactContent(chatId, artifactId, credentials)
            .then((result) => {
                const content = typeof result === 'string' ? result : result.content ?? '';
                const parsed = JSON.parse(content as string);
                if (parsed.type && (VALID_CHART_TYPES as readonly string[]).includes(parsed.type)) {
                    setChartData(parsed);
                } else {
                    setError('Unsupported chart type');
                }
            })
            .catch((e) => setError(e.message ?? 'Failed to load chart'))
            .finally(() => setLoading(false));
    }, [artifactId, chatId, credentials, isChart, renderInline]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const url = await getArtifactDownloadUrl(chatId, artifactId, credentials, name);
            const link = document.createElement('a');
            link.href = url;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Failed to download artifact:', err);
        } finally {
            setDownloading(false);
        }
    };

    // For charts in inline mode, render the chart directly
    if (isChart && renderInline) {
        if (loading) {
            return (
                <div className="cassandra-inline-chart-card">
                    <Box textAlign="center" padding="l">
                        <Spinner /> Loading chart...
                    </Box>
                </div>
            );
        }
        if (error || !chartData) {
            return (
                <div className="cassandra-inline-chart-card">
                    <StatusIndicator type="error">{error || 'Failed to load chart'}</StatusIndicator>
                </div>
            );
        }
        return (
            <div className="cassandra-inline-chart-card">
                <Header headingTagOverride="h5">{chartData.props?.ariaLabel || name}</Header>
                <ChartRenderer
                    type={chartData.type as 'line-chart' | 'bar-chart' | 'area-chart' | 'pie-chart' | 'mixed-chart'}
                    props={{...chartData.props}}
                />
            </div>
        );
    }

    // Card-style preview for all artifacts
    return (
        <div
            className="cassandra-artifact-card"
            onClick={() => onClick?.({artifactId, name, mimeType})}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onClick?.({artifactId, name, mimeType});
            }}
        >
            <div className="cassandra-artifact-card-content">
                <div className="cassandra-artifact-card-icon">{getIcon(mimeType)}</div>
                <div className="cassandra-artifact-card-text">
                    <div className="cassandra-artifact-card-name">{name}</div>
                    <div className="cassandra-artifact-card-meta">
                        {sizeBytes ? `${formatBytes(sizeBytes)} · ` : ''}
                        {getTypeLabel(mimeType)}
                    </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                    <Button
                        iconName="download"
                        variant="normal"
                        loading={downloading}
                        onClick={() => handleDownload()}
                        ariaLabel={`Download ${name}`}
                    />
                </div>
            </div>
        </div>
    );
}
